import type { ViewsService } from './Views.service';
import type { IDisposable } from '@/engine/Utilities/IDisposable';
import { iocContainer, TYPES } from '@/engine/ioc';
import type { ImagesService } from './modules/images/Images.service';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { ImagesMode } from './modules/images/interfaces';
import type { ICenterLineViewModel, IViewItemsViewModelController } from './interfaces';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import * as GeometryHelper from '@/engine/utils/geometryHelper';
import type * as THREE from 'three';

export class CenterlineSelectionManager implements IDisposable {
  private readonly _viewsService: ViewsService;

  private readonly _imagesService: ImagesService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _vmController: IViewItemsViewModelController;

  private _centerlineGeometryController: CenterlineGeometryController | undefined;

  public constructor(imagesService: ImagesService, planningAlgorithm: PlanningAlgorithm, viewsService: ViewsService) {
    this._viewsService = viewsService;
    this._imagesService = imagesService;
    this._planningAlgorithm = planningAlgorithm;

    this._vmController = iocContainer.get<IViewItemsViewModelController>(TYPES.IViewItemsViewModelController);

    const currentView = this._viewsService.currentView;
    if (currentView !== null) {
      this.setActiveCenterLineDescriptor(currentView.getActiveCenterLineDescriptor());
    }
  }

  public dispose(): void {
    this.setActiveCenterLineDescriptor(undefined);
    this._imagesService.setImagesMode(ImagesMode.MPR);
    this._centerlineGeometryController?.dispose();
    this._centerlineGeometryController = undefined;
  }

  public setActiveCenterLineDescriptor(clDescriptor: string | undefined): void {
    const currentView = this._viewsService.currentViewOrThrow;

    const viewMeasurements = currentView.getMeasurements();

    const viewCLs = currentView.getCenterLines();

    const prevClDescriptor = currentView.getActiveCenterLineDescriptor();
    currentView.setActiveCenterLineDescriptor(clDescriptor);

    if (prevClDescriptor !== undefined) {
      const centerLineViewObject = viewCLs.find((item) => item.descriptor === prevClDescriptor);
      centerLineViewObject?.setSelection(false);
    }

    // [chebotarev] !!! dont forget to dispose previous controller
    this._centerlineGeometryController?.dispose();
    this._centerlineGeometryController = undefined;

    if (clDescriptor !== undefined) {
      const clModel = this._planningAlgorithm.getValue(clDescriptor, DT.Centerline)?.value;
      if (clModel === undefined) {
        console.warn('Centerline model is undefined');
        return;
      }
      const currentCL = currentView.getCenterLines().find((cL: ICenterLineViewModel) => cL.descriptor === clDescriptor);
      if (currentCL === undefined) {
        throw new Error('Centerline is undefined');
      }

      this._imagesService.setPolylineInfo(currentCL.alongSliceDescriptor, clModel.points, clModel.normals);
      this._imagesService.setImagesMode(ImagesMode.POLYLINE);

      currentCL.setSelection(true);

      const selectedMeasurement = viewMeasurements.find((ms) => {
        return (
          ms.isSelected &&
          ms.imagesMode === ImagesMode.POLYLINE &&
          ms.syncSliceDescriptor === currentCL.alongSliceDescriptor
        );
      });
      if (selectedMeasurement) {
        this._vmController.locateMeasurementByDescriptor(selectedMeasurement.descriptor);
      } else {
        const middlePoint = clModel.points[Math.round((clModel.points.length - 1) / 2)];
        this._imagesService.requestSliceByPoint(currentCL.alongSliceDescriptor, middlePoint);
      }

      this._centerlineGeometryController = new CenterlineGeometryController(
        currentCL,
        this._planningAlgorithm,
        this._imagesService
      );
    } else {
      this._imagesService.setImagesMode(ImagesMode.MPR);
    }
  }
}

class CenterlineGeometryController implements IDisposable {
  private readonly _imagesService: ImagesService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _centerLine: ICenterLineViewModel;

  private _subscriptions: DisposablesBasket = new DisposablesBasket();

  public constructor(
    centerLine: ICenterLineViewModel,
    planningAlgorithm: PlanningAlgorithm,
    imagesService: ImagesService
  ) {
    this._imagesService = imagesService;
    this._planningAlgorithm = planningAlgorithm;
    this._centerLine = centerLine;

    this._subscriptions.add(
      this._planningAlgorithm.addOnChangedCallbackFor<DT.Centerline>(
        this._centerLine.descriptor,
        this._onCenterlineChanged.bind(this)
      )
    );
  }

  private _onCenterlineChanged(model?: DT.Centerline): void {
    if (model && model.value.points.length > 0) {
      const val = model.value;
      const modified = this._imagesService.setPolylineInfo(
        this._centerLine.alongSliceDescriptor,
        val.points,
        val.normals
      );
      if (modified && this._imagesService.isReady()) {
        const sliceCS = this._imagesService.getSlicePositionInfo(this._centerLine.alongSliceDescriptor)?.sliceCS;
        if (sliceCS === undefined) {
          throw new Error('SliceCS is undefined');
        }
        const nearestPolylinePoint =
          val.points.find((point: THREE.Vector3) => {
            return GeometryHelper.isPointNearPlane(point, sliceCS, 0.15);
          }) ?? model.value.points[model.value.points.length - 1];

        this._imagesService.requestSliceByPoint(this._centerLine.alongSliceDescriptor, nearestPolylinePoint);
      }
    }
  }

  public dispose(): void {
    this._subscriptions.dispose();
    this._imagesService.setPolylineInfo(this._centerLine.alongSliceDescriptor, [], []);
  }
}
